import { useState } from 'react';
import { Bell, Mail } from 'lucide-react';
import type { NotificationCatalogEntry } from '@peles/shared';
import type { Channel, NotificationRule } from '../../api/notificationRouting';
import { SearchableListSelect } from '../common/SearchableListSelect';
import {
  useClearNotificationRule,
  useSetNotificationRule,
} from '../../hooks/useNotificationRouting';
import { CHANNEL_LABEL, DEFAULT_CHOICE, describeChannels, type SendToChoice } from './routingView';

export interface NotificationRuleRowProps {
  entry: NotificationCatalogEntry;
  eventId: string | null;
  rule: NotificationRule | null;
  choices: SendToChoice[];
  canEdit: boolean;
  /** How the office-wide rule sends it, when this event follows that rule. */
  inheritedChannels?: readonly Channel[];
}

const CHANNEL_ICON: Record<Channel, typeof Bell> = { in_app: Bell, email: Mail };

/**
 * One notification and who it goes to: its name, what sets it off, a
 * "Sends to" picker (the default, or a group) and — once a group is chosen —
 * how it reaches them. Every change is saved as it is made.
 */
export function NotificationRuleRow({
  entry,
  eventId,
  rule,
  choices,
  canEdit,
  inheritedChannels,
}: NotificationRuleRowProps) {
  const setRule = useSetNotificationRule();
  const clearRule = useClearNotificationRule();
  const [error, setError] = useState<string | null>(null);
  const busy = setRule.isPending || clearRule.isPending;
  const selected = rule?.groupId ?? DEFAULT_CHOICE;
  const channels = rule?.channels ?? [];

  const run = (work: Promise<unknown>) => {
    setError(null);
    work.catch((err: unknown) =>
      setError(err instanceof Error ? err.message : 'That could not be saved.'),
    );
  };

  const choose = (value: string | string[]) => {
    const picked = Array.isArray(value) ? value[0] : value;
    const groupId = picked || DEFAULT_CHOICE;
    if (groupId === selected) return;
    if (groupId === DEFAULT_CHOICE) {
      run(clearRule.mutateAsync({ eventId, notification: entry.key }));
      return;
    }
    run(
      setRule.mutateAsync({
        eventId,
        notification: entry.key,
        groupId,
        // Keep how it goes out now: the rule's own channels, else the inherited
        // office-wide rule's (an email-only rule stays email-only), else every way.
        channels: rule
          ? rule.channels
          : inheritedChannels && inheritedChannels.length > 0
            ? entry.channels.filter((c) => inheritedChannels.includes(c))
            : [...entry.channels],
      }),
    );
  };

  const toggle = (channel: Channel) => {
    if (!rule) return;
    const next = channels.includes(channel)
      ? channels.filter((c) => c !== channel)
      : [...channels, channel];
    run(
      setRule.mutateAsync({
        eventId,
        notification: entry.key,
        groupId: rule.groupId,
        channels: next,
      }),
    );
  };

  const selectId = `send-to-${entry.key}`;

  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900">{entry.label}</div>
          <div className="text-xs text-gray-500">{entry.trigger}</div>
        </div>
        <div className="w-full md:w-80">
          <label htmlFor={selectId} className="sr-only">
            {`Who gets “${entry.label}”`}
          </label>
          <SearchableListSelect
            inputId={selectId}
            items={choices}
            value={selected}
            onChange={choose}
            getValue={(c) => c.id}
            getLabel={(c) => c.label}
            getSubtitle={(c) => c.subtitle}
            disabled={!canEdit || busy}
            clearable={false}
            placeholder="Choose who gets it"
            ariaLabel={`Who gets “${entry.label}”`}
          />
        </div>
        <div className="flex items-center gap-2 md:w-44 md:justify-end">
          {rule ? (
            entry.channels.map((channel) => {
              const on = channels.includes(channel);
              const Icon = CHANNEL_ICON[channel];
              // Never both off: choosing "Default" is how a rule is taken away.
              const last = on && channels.length === 1;
              return (
                <button
                  key={channel}
                  type="button"
                  aria-pressed={on}
                  disabled={!canEdit || busy || last}
                  title={
                    last
                      ? 'At least one way stays on — choose Default to undo the group'
                      : undefined
                  }
                  onClick={() => toggle(channel)}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 text-xs font-medium sm:min-h-8 ${
                    on
                      ? 'border-primary-200 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-400 line-through'
                  } disabled:cursor-not-allowed disabled:opacity-70`}
                >
                  <Icon size={14} aria-hidden="true" />
                  {CHANNEL_LABEL[channel]}
                </button>
              );
            })
          ) : (
            <span className="text-xs text-gray-400">
              {describeChannels(inheritedChannels ?? entry.channels)}
            </span>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </li>
  );
}
